/**
 * Portfolio / orders / fills rows under a venue scope (#689): in-venue outcome rows by default, plus a session
 * toggle "Show other venues" (read-only). Non-outcome rows (USDC, spot, perps) are never venue-filtered.
 * Outcomes missing from outcomeMeta (settled, or meta still loading) cannot be attributed and stay visible.
 */
import { useCallback, useMemo } from 'react';
import { useOutcomeMeta } from '../account/useMarketData';
import { fromCoin } from '../hl/outcomes';
import { outcomeInVenue, venueIndex } from './scope';
import { useShowOtherVenues, useVenueScope } from './useVenueScope';

export interface VenueRows {
  venue: string | null;
  scoped: boolean;
  showOther: boolean;
  setShowOther: (v: boolean) => void;
  /** True for an outcome known to belong to another venue. */
  isOtherOutcome: (outcome: number | null | undefined) => boolean;
  /** Same, for an order / fill / balance coin ("#E", "+E" or asset id). Non-outcome coins are never "other". */
  isOtherCoin: (coin: string) => boolean;
}

export function useVenueRows(): VenueRows {
  const { venue } = useVenueScope();
  const meta = useOutcomeMeta(venue !== null);
  const [showOther, setShowOther] = useShowOtherVenues();
  const index = useMemo(() => venueIndex(meta?.outcomes), [meta]);
  const isOtherOutcome = useCallback(
    (outcome: number | null | undefined) =>
      venue !== null && typeof outcome === 'number' && outcomeInVenue(index, outcome, venue) === false,
    [index, venue],
  );
  const isOtherCoin = useCallback((coin: string) => isOtherOutcome(fromCoin(coin)?.outcome), [isOtherOutcome]);
  return { venue, scoped: venue !== null, showOther, setShowOther, isOtherOutcome, isOtherCoin };
}

/** Filters rows by venue; `hidden` counts other-venue rows currently hidden. */
export function useVenueFiltered<T>(rows: readonly T[], isOther: (r: T) => boolean, v: VenueRows) {
  return useMemo(() => {
    if (!v.scoped) return { visible: rows as T[], hidden: 0, otherCount: 0 };
    const other = rows.filter(isOther).length;
    return {
      visible: v.showOther ? (rows as T[]) : rows.filter((r) => !isOther(r)),
      hidden: v.showOther ? 0 : other,
      otherCount: other,
    };
  }, [rows, isOther, v.scoped, v.showOther]);
}

/** Rows that carry an outcome id (balances / positions): venue-filtered with the shared toggle. */
export function useVenueOutcomeRows<T extends { outcome: number }>(rows: readonly T[]) {
  const v = useVenueRows();
  const { isOtherOutcome } = v;
  const isOther = useCallback((r: T) => isOtherOutcome(r.outcome), [isOtherOutcome]);
  return { v, ...useVenueFiltered(rows, isOther, v) };
}
